/**
 * Kora tokens for TypeScript (KORA-112). `kora.css` stays the single source: this module parses
 * its :root block at build time, so the Naive UI overrides in Kora Web and the Admin
 * Panel can never drift from the CSS.
 */
import koraCss from './kora.css?raw'
import { parseRootTokens, tokenReader, type Tokens } from './rootTokens'

export { parseRootTokens }
export type KoraTokens = Tokens

export const koraTokens: KoraTokens = parseRootTokens(koraCss)

/** A concrete token value. Throws when the token is missing or is a var() reference. */
export const koraToken: (name: string) => string = tokenReader('Kora', koraTokens)

/** Naive UI global theme overrides, structurally typed so this file needs no naive-ui import. */
export function koraNaiveThemeOverrides(): {
  common: Record<string, string>
  Button: Record<string, string>
  Empty: Record<string, string>
} {
  const statusAll = (
    key: 'success' | 'warning' | 'error' | 'info',
    token: string
  ): Record<string, string> => {
    const color = koraToken(token)
    const hover = koraToken(`${token}-hover`)
    return {
      [`${key}Color`]: color,
      [`${key}ColorHover`]: hover,
      [`${key}ColorPressed`]: hover,
      [`${key}ColorSuppl`]: color
    }
  }
  return {
    common: {
      primaryColor: koraToken('--color-primary'),
      primaryColorHover: koraToken('--color-primary-hover'),
      primaryColorPressed: koraToken('--color-primary-pressed'),
      primaryColorSuppl: koraToken('--color-primary-hover'),
      ...statusAll('success', '--color-success'),
      ...statusAll('warning', '--color-warning'),
      ...statusAll('error', '--color-error'),
      ...statusAll('info', '--color-info'),
      bodyColor: koraToken('--color-background'),
      cardColor: koraToken('--color-surface'),
      modalColor: koraToken('--color-surface'),
      popoverColor: koraToken('--color-surface'),
      textColorBase: koraToken('--color-text-primary'),
      // NGlobalStyle paints the body with textColor2 and Naive's controls read their normal text
      // from it, so both stay Ink; textColor3 is placeholder and disabled text.
      textColor1: koraToken('--color-text-primary'),
      textColor2: koraToken('--color-text-primary'),
      textColor3: koraToken('--color-text-muted'),
      fontSize: koraToken('--text-base'),
      lineHeight: '1.5',
      // Naive's default gray (#c2c2c2) is about 1.7:1 on white; the placeholder, the dropdown and
      // clear icons and (through Empty below) the empty-state text all inherit it.
      placeholderColor: koraToken('--color-text-muted'),
      iconColor: koraToken('--color-text-muted'),
      iconColorHover: koraToken('--color-text-secondary'),
      iconColorPressed: koraToken('--color-text-secondary'),
      clearColor: koraToken('--color-text-muted'),
      clearColorHover: koraToken('--color-text-secondary'),
      clearColorPressed: koraToken('--color-text-secondary'),
      borderColor: koraToken('--color-border-strong'),
      dividerColor: koraToken('--color-border'),
      fontFamily: koraToken('--font-sans'),
      fontFamilyMono: koraToken('--font-mono'),
      borderRadius: koraToken('--radius-md'),
      borderRadiusSmall: koraToken('--radius-sm')
    },
    Button: {
      fontWeight: '600'
    },
    Empty: {
      textColor: koraToken('--color-text-muted')
    }
  }
}
